// Loads the console's data from a source and keeps it fresh: on every
// Realtime change to orders or positions, and every 30 s as a safety net.
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Source } from './api';
import type { Live, OpsOrder, Overview } from './types';

export interface Data {
  overview: Overview | null;
  orders: OpsOrder[] | null;
  live: Live | null;
  error: string | null;
  updatedAt: Date | null;
  /** How many of the first load's three reads have answered (0 to 3), for the splash's bar. */
  parts: number;
  reload(): void;
}

export function useData(src: Source): Data {
  const [overview, setOverview] = useState<Overview | null>(null);
  const [orders, setOrders] = useState<OpsOrder[] | null>(null);
  const [live, setLive] = useState<Live | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<Date | null>(null);
  const [parts, setParts] = useState(0);
  const first = useRef(true);

  const reload = useCallback(() => {
    // Only the first load moves the splash's bar, one step per read that answers.
    const tick = <T,>(p: Promise<T>) => (first.current ? p.then((v) => { setParts((n) => Math.min(3, n + 1)); return v; }) : p);
    Promise.all([tick(src.overview()), tick(src.orders()), tick(src.live())])
      .then(([ov, os, lv]) => { first.current = false; setOverview(ov); setOrders(os); setLive(lv); setError(null); setUpdatedAt(new Date()); })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String((e as { message?: string })?.message ?? e)));
  }, [src]);

  useEffect(() => {
    reload();
    const off = src.subscribe(reload);
    const t = setInterval(() => { if (!document.hidden) reload(); }, 30_000);
    return () => { off(); clearInterval(t); };
  }, [src, reload]);

  return { overview, orders, live, error, updatedAt, parts, reload };
}
